/*
 *
 * The content of this file can be edited freely, but to maintain upgradability
 * this file should not be renamed and should always export an Angular module named
 * `AppRoutingModule`.
 *
 *
 */
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { EntitlementsGuard } from '@backbase/foundation-ang/entitlements';
import { AuthGuard } from '@backbase/shared/feature/auth';
import { SharedUserContextGuard } from '@backbase/shared/feature/user-context';

import { PERMISSIONS } from './auth/permissions';
import { IdentityJourneyWrapperComponent } from './journeys/wrapper-component/journey-wrapper.component';
import { LayoutComponent } from './layout/layout.component';
import { FeatureFlagsAndLimitsGuard } from '@sbk/feature/feature-flags-and-limits';
import { TotalDowntimePageComponent } from '@sbk/feature/feature-flags-and-limits';

//TODO: Find a more elegant solution to decide what landing page to choose
// in the event that the default one is not available due to entitlements

// NOTE: This file contains ~129 commented-out route blocks (~30% of the file).
// Cleanup is deferred pending product/feature-team input on which are
// temporarily disabled vs truly dead. See PLAN.md §1 for context.

const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'my-accounts',
  },
  {
    path: 'select-context',
    loadChildren: () => import('./user-context/user-context.module').then((m) => m.UserContextModule),
    data: {
      title: $localize`:@@context-selection.nav.item.title:Select Context`,
    },
    canActivate: [AuthGuard],
  },
  {
    path: '',
    component: LayoutComponent,
    children: [
      // {
      //   path: 'dashboard',
      //   loadChildren: () => import('./journeys/dashboard/wrapper').then((m) => m.DashboardJourneyWrapperModule),
      //   data: {
      //     entitlements: PERMISSIONS.canViewDashboard,
      //     cssClasses: ['bb-ret-container-cols-2'],
      //     redirectTo: 'my-accounts',
      //   },
      //   title: $localize`:@@dashboard.nav.item.title:Dashboard`,
      // },
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'my-accounts',
      },
      {
        path: 'insights-dashboard',
        data: {
          entitlements: PERMISSIONS.canViewInsightsDashboard,
          cssClasses: ['bb-ret-container-cols-2'],
        },
        title: $localize`:@@insights.nav.item.title.insightsDashboard:Insights Dashboard`,
        loadChildren: () =>
          import('./journeys/insights-dashboard/insights-dashboard-journey-bundle.module').then(
            (m) => m.InsightsDashboardJourneyBundleModule,
          ),
      },
      /*********************************************************************************************** */
      /************************************  CUSTOM ROUTES  ****************************************** */
      /*********************************************************************************************** */
      {
        path: 'self-service/download-statements',
        canActivate: [FeatureFlagsAndLimitsGuard],
        loadChildren: () =>
          import('./journeys/sbk-account-statements/bundle-sbk-accounts-statement-journey.module').then(
            (m) => m.SbkAccountStatementJourneyBundleModule,
          ),
        data: {
          title: $localize`:@@account-statements.nav.item.title:Account statements - Retail Banking`,
          entitlements: PERMISSIONS.canViewAccountStatements,
        },
      },
      {
        path: 'total-downtime',
        component: TotalDowntimePageComponent,
        data: {
          title: $localize`Total downtime - Retail Banking`
        },
      },
      /*********************************************************************************************** */
      /*********************************************************************************************** */
      /*********************************************************************************************** */

      {
        path: 'my-accounts',
        data: {
          title: $localize`:@@accounts.nav.item.title:My accounts - Retail Banking`,
          entitlements: PERMISSIONS.canViewMyAccounts,
          cssClasses: ['container-fluid', 'container'],
          redirectTo: 'transfers/make-a-transfer',
        },
        canActivate: [FeatureFlagsAndLimitsGuard],
        children: [
          {
            path: '',
            loadChildren: () =>
              import(
                './journeys/accounts/accounts-transactions-wrapper/wrapper-accounts-transactions-journey.module'
              ).then((m) => m.AccountsTransactionsJourneyBundleModule),
          },
          // {
          //   path: 'loans/details',
          //   children: [
          //     {
          //       path: '',
          //       loadChildren: () =>
          //         import('./journeys/accounts/loans/loans-journey-bundle.module').then(
          //           (m) => m.LoansJourneyBundleModule,
          //         ),
          //       data: {
          //         title: $localize`:@@loans.nav.item.title:Loans - Accounts - Retail Banking`,
          //         entitlements: PERMISSIONS.canViewLoans,
          //       },
          //     },
          //     {
          //       path: ':selectedAccount',
          //       loadChildren: () =>
          //         import('./journeys/accounts/loans/loan-payments/loan-payment-wrapper-bundle.module').then(
          //           (m) => m.LoanPaymentJourneyWrapperBundleModule,
          //         ),
          //       data: {
          //         title: $localize`:@@loans-item.nav.item.title:Loan - Accounts - Retail Banking`,
          //         entitlements: PERMISSIONS.canViewLoans,
          //       },
          //     },
          //   ],
          // },
        ],
      },

      // {
      //   path: 'pockets',
      //   loadChildren: () =>
      //     import('./journeys/accounts/manage-pockets-journey.module').then((m) => m.ManagePocketsJourneyBundleModule),
      //   data: {
      //     title: $localize`:@@accounts.nav.item.title:Pockets`,
      //     entitlements: PERMISSIONS.canViewManagePockets,
      //     cssClasses: ['container--fixed-width mx-auto'],
      //     redirectTo: 'pockets',
      //   },
      // },
      {
        path: 'transfers',
        data: { cssClasses: ['container--fixed-width mx-auto'] },
        canActivate: [FeatureFlagsAndLimitsGuard],
        children: [
          {
            path: 'activity',
            loadChildren: () =>
              import('./journeys/transfers/upcoming-and-history-payments.module').then(
                (m) => m.ManageUpcomingAndHistoricalPaymentsJourneyBundleModule,
              ),
            data: {
              title: $localize`:@@activity.nav.item.title:Activity - Transfer - Retail Banking`,
              entitlements: PERMISSIONS.canViewScheduledTransfers,
            },
          },
          {
            path: 'connected-accounts',
            loadChildren: () =>
              import('./journeys/transfers/connect-external-accounts-journey.module').then(
                (m) => m.ConnectExternalAccountsJourneyBundleModule,
              ),
            data: {
              title: $localize`:@@connected-accounts.nav.item.title:Connected accounts - Transfer - Retail Banking`,
              entitlements: PERMISSIONS.canViewConnectedAccounts,
            },
          },
          {
            path: 'local-payments',
            loadChildren: () =>
              import('./journeys/transfers/wrapper-initiate-payment-journey/initiate-local-payments-wrapper-bundle.module').then((m) => m.LocalPaymentsTransferJourneyModule),
          },
          {
            path: 'mobile-payments',
            loadChildren: () =>
              import('./journeys/transfers/wrapper-initiate-payment-journey/initiate-mobile-payments-wrapper-bundle.module').then((m) => m.MobilePaymentsTransferJourneyModule),
          },
          {
            path: '',
            loadChildren: () =>
              import(
                './journeys/transfers/wrapper-initiate-payment-journey/initiate-payment-wrapper-bundle.module'
              ).then((m) => m.InitiatePaymentWrapperBundleModule),
          },
        ],
      },

      {
        path: 'loans',
        data: {
          title: 'Loans - Retail Banking',
          cssClasses: ['container--fixed-width mx-auto']
        },
        canActivate: [FeatureFlagsAndLimitsGuard],
        children: [
          {
            path: '',
            loadChildren: () =>
              import(
                './journeys/loans/loans-wrapper-bundle.module'
              ).then((m) => m.LoansWrapperBundleModule),
          },
        ],
      },

      {
        path: 'invest',
        data: { cssClasses: ['container--fixed-width mx-auto'] },
        canActivate: [FeatureFlagsAndLimitsGuard],
        children: [
          {
            path: '',
            loadChildren: () => import('./journeys/invest/wrapper-invest-journey.module').then((m) => m.WrapperInvestJourneyModule),
          }
        ],
      },

      {
        path: 'bill-payments',
        data: {
          title: 'Bill payments - Retail Banking',
          entitlements: PERMISSIONS.canViewMakeTransfer,
          cssClasses: ['container--fixed-width mx-auto']
        },
        canActivate: [FeatureFlagsAndLimitsGuard],
        children: [
          {
            path: '',
            loadChildren: () =>
              import(
                './journeys/bill-pay/wrapper-bill-payments.module'
              ).then((m) => m.WrapperBillPaymentsJourneyModule),
          },
        ],
      },

      // {
      //   path: 'insights',
      //   data: { cssClasses: ['container--fixed-width mx-auto'] },
      //   children: [
      //     {
      //       path: 'cashflow',
      //       loadChildren: () =>
      //         import('./journeys/insights/bundle-turnovers.module').then((m) => m.TurnoversJourneyBundleModule),
      //       data: {
      //         title: $localize`:@@cash-flow.nav.item.title:Cash flow - Analytics - Retail Banking`,
      //       },
      //     },
      //     {
      //       path: 'income-analysis',
      //       loadChildren: () =>
      //         import('./journeys/insights/bundle-income-analysis.module').then((m) => m.IncomeAnalysisBundleModule),
      //       data: {
      //         title: $localize`:@@income-analysis.nav.item.title:Income analysis - Analytics - Retail Banking`,
      //       },
      //     },
      //     {
      //       path: 'spending-analysis',
      //       loadChildren: () =>
      //         import('./journeys/insights/bundle-spending-analysis.module').then((m) => m.SpendingAnalysisBundleModule),
      //       data: {
      //         title: $localize`:@@spending-analysis.nav.item.title:Spending analysis - Analytics - Retail Banking`,
      //       },
      //     },
      //   ],
      // },
      {
        path: 'self-service',
        data: { cssClasses: ['container--fixed-width mx-auto'] },
        canActivate: [FeatureFlagsAndLimitsGuard],
        children: [
          {
            path: 'profile',
            loadChildren: () =>
              import('./journeys/self-service/bundle-profile-journey.module').then(
                (m) => m.SelfServiceJourneyBundleModule,
              ),
            data: {
              title: $localize`:@@my-profile.nav.item.title:My profile - Self services - Retail Banking`,
            },
          },
          // {
          //   path: 'authorized-users',
          //   loadChildren: () =>
          //     import('./journeys/self-service/bundle-authorized-users.module').then(
          //       (m) => m.AuthorizedUsersJourneyBundleModule,
          //     ),
          //   data: {
          //     title: $localize`:@@authorized-users.nav.item.title:Authorized users - Self services - Retail Banking`,
          //     entitlements: PERMISSIONS.canViewAuthorizedUsers,
          //   },
          // },
          // {
          //   path: 'manage-cards',
          //   loadChildren: () =>
          //     import('./journeys/self-service/bundle-cards-management-journey.module').then(
          //       (m) => m.CardsManagementJourneyBundleModule,
          //     ),
          //   data: {
          //     title: $localize`:@@manage-cards.nav.item.title:Manage cards - Self services - Retail Banking`,
          //     entitlements: PERMISSIONS.canViewManageCards,
          //   },
          // },
          // {
          //   path: 'product-list',
          //   loadChildren: () =>
          //     import('./journeys/self-service/bundle-actions-retail-notification-preferences-journey.module').then(
          //       (m) => m.ActionsRetailNotificationPreferencesJourneyBundleModule,
          //     ),
          //   data: {
          //     title: $localize`:@@manage-notifications.nav.item.title:Manage notifications - Self services - Retail Banking`,
          //     entitlements: PERMISSIONS.canViewManageNotifications,
          //   },
          // },
          {
            path: 'manage-contacts',
            loadChildren: () =>
              import('./journeys/self-service/bundle-contact-journey.module').then(
                (m) => m.ContactManagerJourneyBundleModule,
              ),
            data: {
              title: $localize`:@@manage-contacts.nav.item.title:Manage contacts - Self services - Retail Banking`,
              entitlements: PERMISSIONS.canViewManageContacts,
            },
          },
          {
            path: 'cards',
            loadChildren: () =>
              import('./journeys/self-service/view-card-details/view-card-details.module').then(
                (m) => m.ViewCardDetailsModule,
              ),
            data: {
              title: $localize`:@@view-card-details.nav.item.title:View Card Details - Self services - Retail Banking`,
              entitlements: PERMISSIONS.canViewManageCards,
            },
          },
          {
            path: 'stop-checks',
            loadChildren: () =>
              import('./journeys/self-service/wrapper-stop-checks-journey/bundle-stop-checks-wrapper.module').then(
                (m) => m.StopChecksJourneyWrapperBundleModule,
              ),
            data: {
              title: $localize`:@@stop-checks.nav.item.title:Stop checks - Self services - Retail Banking`,
              entitlements: PERMISSIONS.canViewStopChecks,
            },
          },
        ],
      },
      {
        path: 'more',
        data: { cssClasses: ['container--fixed-width mx-auto'] },
        children: [
          // {
          //   path: 'budgets',
          //   loadChildren: () => import('./journeys/more/bundle-budget.module').then((m) => m.BudgetJourneyBundleModule),
          //   data: {
          //     title: $localize`:@@budgets.nav.item.title:Budgets - Retail Banking`,
          //     entitlements: PERMISSIONS.canViewBudgets,
          //   },
          // },
          {
            path: 'messages',
            loadChildren: () =>
              import('./journeys/more/bundle-messages-client-inbox-journey-bundle.module').then(
                (m) => m.MessagesClientInboxJourneyBundleModule,
              ),
            data: {
              title: $localize`:@@messages.nav.item.title:Messages - Retail Banking`,
            },
          },
          // {
          //   path: 'find-us',
          //   loadChildren: () => import('./journeys/more/bundle-places.module').then((m) => m.PlacesJourneyBundleModule),
          //   data: {
          //     title: $localize`:@@places.nav.item.title:Find branches and ATMs - Retail Banking`,
          //     entitlements: PERMISSIONS.canViewPlaces,
          //   },
          // },
          {
            path: 'get-in-touch',
            loadChildren: () => import('./journeys/more/bundle-get-in-touch.module').then((m) => m.GetInTouchJourneyBundleModule),
            data: {
              title: 'Get in Touch - Retail Banking'
            },
          },
        ],
      },
    ],
    canActivate: [AuthGuard, SharedUserContextGuard],
    canActivateChild: [EntitlementsGuard],
  },
  {
    path: 'consent',
    loadChildren: () =>
      import('./journeys/consent/bundle-consent-journey.module').then((m) => m.ConsentJourneyBundleModule),
  },
  {
    path: 'enrollment',
    component: IdentityJourneyWrapperComponent,
    canActivate: [FeatureFlagsAndLimitsGuard],
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./journeys/self-enrollment/self-enrollment-bundle.module').then(
            (m) => m.SelfEnrollmentJourneyBundleModule,
          ),
      },
    ],
  },
  {
    path: 'password-reset',
    component: IdentityJourneyWrapperComponent,
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./journeys/password-reset/password-reset-bundle.module').then(
            (m) => m.PasswordResetJourneyBundleModule,
          ),
      },
    ],
  },
  { path: '**', pathMatch: 'full', redirectTo: 'my-accounts' },
];

@NgModule({
  declarations: [IdentityJourneyWrapperComponent],
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'enabled',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule { }
